import { type ExternalToast, toast } from 'sonner'

type ToastOptions = Pick<ExternalToast, 'id' | 'duration'>

/**
 * Writes to the clipboard and reports the outcome as a toast — the one home for
 * copy semantics, shared by `CopyButton` and inline copy affordances.
 *
 * @returns whether the value reached the clipboard.
 */
export async function copyTextToClipboard(
  value: string,
  {
    successMessage = 'Copied to clipboard',
    errorMessage,
    toastOptions
  }: {
    successMessage?: string
    errorMessage?: string
    /** A function is called when the toast is shown, after the clipboard write settles. */
    toastOptions?: ToastOptions | (() => ToastOptions)
  } = {}
): Promise<boolean> {
  const notify = (kind: 'success' | 'error', message: string) => {
    if (!toastOptions) return toast[kind](message)
    return toast[kind](message, typeof toastOptions === 'function' ? toastOptions() : toastOptions)
  }

  if (!navigator.clipboard) {
    console.warn('Clipboard API not supported')
    // Retrying can't help without the API, so the default asks for a manual copy.
    notify('error', errorMessage ?? "Your browser can't copy here. Select the text and copy it.")
    return false
  }

  try {
    await navigator.clipboard.writeText(value)
    notify('success', successMessage)
    return true
  } catch (error) {
    console.error('Failed to copy text:', error)
    notify('error', errorMessage ?? "Couldn't copy. Try again.")
    return false
  }
}
